import React, {useState, useEffect, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import {Modal} from '@/components/atoms';
import CustomDataTable from '@/components/organisms/CustomDataTable/CustomDataTable';
import {getProductList} from '@/services/ProductsService/product';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import {useDataTable} from '@/hooks/useDataTable-hook';
import {
  PRODUCT_SELECTION_COLUMNS,
  PRODUCT_SELECTION_COLUMNS_WITH_GTIN
} from './config';
import {showGtinFeature} from '@/utils/marketFeatures';
import {useAppSelector} from '@/store';
import ToasterAlert from '@/components/atoms/Toast/toasterAlert';
import {REWARD_TYPES} from '@/utils/constants/rewardsConstants';
import {logger} from '@/helpers/logger';

/**
 * ProductSelectionModal
 * - Displays a modal with searchable, sortable, paginated product table.
 * - Supports persistent multi-page selection.
 */
const ProductSelectionModal = ({
  showModal,
  onCloseModal,
  onSelectProducts,
  selectedProducts,
  rewardType
}) => {
  // Table state (via hook)
  const {
    setPageIndex,
    setSearchArry,
    setSortingObj,
    pageIndex,
    searchArr,
    sortingObj,
    handleResetAll
  } = useDataTable();

  // Local state
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [tableKey, setTableKey] = useState(0);
  const [isCancel, setIsCancel] = useState(false);
  const countryCode = useAppSelector((state) => state.countryCode).countryCode;

  // Column configuration for CustomDataTable
  const showGtin = showGtinFeature(countryCode);

  // Determine columns to show based on feature flag
  const columns = showGtin
    ? PRODUCT_SELECTION_COLUMNS_WITH_GTIN
    : PRODUCT_SELECTION_COLUMNS;

  const selectedMapRef = useRef(new Map());
  const isMounted = useRef(false);

  /** ---------- Data Fetch ---------- */
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pageIndex,
        pageSize
      });

      if (sortingObj?.column) {
        queryParams.append('sortBy', sortingObj.column);
        queryParams.append('order', sortingObj.direction);
      }

      const response = await getProductList(queryParams.toString(), searchArr);
      const data = response?.data ?? {};
      const items = data.items ?? data;
      const total = data.totalRecords ?? response?.totalRecords ?? 0;

      if (!isMounted.current) return;
      setProducts(Array.isArray(items) ? items : []);
      setTotalRecords(Number.isFinite(total) ? total : 0);
    } catch (error) {
      if (isMounted.current) {
        logger.error('Error fetching products:', error);
        setProducts([]);
        setTotalRecords(0);
      }
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [pageIndex, pageSize, searchArr, sortingObj]);

  // Fetch products when modal opens or filters change
  useEffect(() => {
    if (!showModal) return;
    isMounted.current = true;
    fetchProducts();
    return () => {
      isMounted.current = false;
    };
  }, [showModal, fetchProducts]);

  // Reset table key when modal opens (forces re-init)
  useEffect(() => {
    if (showModal) setTableKey((prev) => prev + 1);
  }, [showModal]);

  /** ---------- Handlers ---------- */

  const handleCloseModal = () => {
    selectedMapRef.current = new Map();
    setSelectedRows([]);
    handleResetAll();
    onCloseModal();
  };

  const handleCancelClick = () => {
    setIsCancel(true);
  };

  const handleCancelConfirm = () => {
    setIsCancel(false);
    handleCloseModal();
  };

  const handleCancelAbort = () => {
    setIsCancel(false);
    const allSelected = Array.from(selectedMapRef.current.values());
    setSelectedRows(allSelected);
  };

  useEffect(() => {
    const list = Array.isArray(selectedProducts) ? selectedProducts : [];

    const normalized = list.map((p) => {
      if (typeof p === 'string') {
        return {productId: p, productName: ''};
      }

      return {
        productId: p.productId ?? p.value ?? '',
        productName: p.productName ?? p.label ?? ''
      };
    });

    // Build selection map keyed by productId
    selectedMapRef.current = new Map(
      normalized
        .filter((p) => Boolean(p.productId))
        .map((p) => [p.productId, p])
    );

    // Initialize selected rows for the table
    setSelectedRows(normalized);
  }, [selectedProducts]);

  const handleRowSelect = (selectedRowsData) => {
    const newSelected = selectedRowsData || [];

    const newIds = new Set(newSelected.map((r) => r.productId).filter(Boolean));
    const currentPageIds = new Set(
      products.map((r) => r.productId).filter(Boolean)
    );

    // Remove unselected IDs from current page
    currentPageIds.forEach((id) => {
      if (!newIds.has(id)) {
        selectedMapRef.current.delete(id);
      }
    });

    // Add new selected IDs
    newIds.forEach((id) => {
      const record = products.find((p) => p.productId === id);
      if (record) selectedMapRef.current.set(id, record);
    });

    // Refresh visible selection
    const pageSelected = products.filter((p) => {
      const id = p.productId;
      return id && selectedMapRef.current.has(id);
    });

    setSelectedRows(pageSelected);
  };

  const handleSelectProducts = () => {
    const selectedRecords = Array.from(selectedMapRef.current.values());
    onSelectProducts(selectedRecords);
    handleCloseModal();
  };

  /** ---------- Render ---------- */

  // Only show single selection validation for Product and PepVoucher Product types
  const shouldEnforceSingleSelection =
    rewardType === REWARD_TYPES.PRODUCT ||
    rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT;

  const isSelectDisabled = shouldEnforceSingleSelection
    ? !(selectedMapRef.current && selectedMapRef.current.size === 1)
    : !(selectedMapRef.current && selectedMapRef.current.size > 0);

  const showMultipleSelectionAlert =
    shouldEnforceSingleSelection &&
    selectedMapRef.current &&
    selectedMapRef.current.size > 1;

  const getModalConfig = () => {
    if (isCancel) {
      return {
        showModal: isCancel,
        type: MODAL_TYPE.SMALL,
        title: 'Are you sure you want to cancel?',
        text: 'The selection will not be saved.',
        isDark: true,
        showPrimaryButton: true,
        primaryButtonText: 'Yes',
        onClickPrimaryButton: handleCancelConfirm,
        showSecondaryButton: true,
        secondaryButtonText: 'No',
        onClickSecondaryButton: handleCancelAbort,
        onCloseModal: handleCancelAbort
      };
    }

    return {
      type: MODAL_TYPE.MEDIUM,
      hideCloseButton: true,
      showModal: showModal,
      title: 'Product ID List',
      isDark: true,
      showSecondaryButton: true,
      secondaryButtonText: 'Cancel',
      onClickSecondaryButton: handleCancelClick,
      showPrimaryButton: true,
      primaryButtonText: 'Select',
      onClickPrimaryButton: handleSelectProducts,
      primaryButtonDisabled: isSelectDisabled,
      content: (
        <>
          {showMultipleSelectionAlert && (
            <div className='mb-4'>
              <ToasterAlert
                type='warning'
                message='Please select only one product to continue.'
                showCloseButton={false}
                show={showMultipleSelectionAlert}
              />
            </div>
          )}

          <div className='flex flex-1 flex-col'>
            <CustomDataTable
              key={tableKey}
              columns={columns}
              records={products}
              isLoading={isLoading}
              pageSize={pageSize}
              totalRecords={totalRecords}
              paginationConfig={{
                pageIndex,
                setPageIndex
              }}
              sortingConfig={{
                handleSorting: setSortingObj,
                initialSortConfig: sortingObj
              }}
              filterConfig={{
                setSearchArry,
                onResetAll: handleResetAll
              }}
              selectionConfig={{
                onRowSelect: handleRowSelect,
                initialRowSelection: selectedRows,
                hideSelectAll: true,
                resetSelectionOnFilter: false
              }}
              toolbarConfig={{
                hideToolbar: true,
                showActionMenu: false
              }}
              displayConfig={{
                tableMinWidth: 400
              }}
            />
          </div>
        </>
      )
    };
  };

  const modalConfig = getModalConfig();

  return <Modal {...modalConfig} />;
};

// Add rewardType to PropTypes
ProductSelectionModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onSelectProducts: PropTypes.func.isRequired,
  selectedProducts: PropTypes.array,
  rewardType: PropTypes.string // Add this new prop
};

export default ProductSelectionModal;
